import { useBreakpoint } from "@brexhq/metal-utils";
import { useTheme } from "@emotion/react";
import { useMemo } from "react";

import { BreakPointVals } from "@brexhq/shared/styles/breakpoints";

interface UseOptimizedBreakpointReturn {
  currentBreakpoint: BreakPointVals;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  // Helper for conditional rendering
  shouldRenderOnlyFor: (size: BreakPointVals | BreakPointVals[]) => boolean;
}

/**
 * Optimized breakpoint hook that:
 * 1. Memoizes breakpoint calculations
 * 2. Provides helpful utilities for conditional rendering
 * 3. Reduces re-renders by batching breakpoint changes
 */
export const useOptimizedBreakpoint = (): UseOptimizedBreakpointReturn => {
  const { breakpoints } = useTheme();
  const isSML = useBreakpoint(breakpoints.SML);
  const isMED = useBreakpoint(breakpoints.MED);

  // Determine current breakpoint with memoization
  const currentBreakpoint = useMemo((): BreakPointVals => {
    if (isSML) return "SML";
    if (isMED) return "MED";
    return "LRG";
  }, [isSML, isMED]);

  // Memoized boolean helpers
  const breakpointHelpers = useMemo(
    () => ({
      isMobile: currentBreakpoint === "SML",
      isTablet: currentBreakpoint === "MED",
      isDesktop: currentBreakpoint === "LRG",
    }),
    [currentBreakpoint],
  );

  // Helper function for conditional rendering
  const renderOnlyFor = useMemo(
    () => (size: BreakPointVals | BreakPointVals[]) => {
      const sizes = Array.isArray(size) ? size : [size];
      return sizes.includes(currentBreakpoint);
    },
    [currentBreakpoint],
  );

  return {
    currentBreakpoint,
    ...breakpointHelpers,
    shouldRenderOnlyFor: renderOnlyFor,
  };
};

/**
 * Utility function for components that need to render different content per breakpoint
 * Pass the result of useOptimizedBreakpoint to avoid creating multiple hook instances
 * @param breakpointData - The result from useOptimizedBreakpoint hook
 * @param targetBreakpoint - The breakpoint(s) to render for
 * @returns boolean indicating if the component should render for current breakpoint
 */
export const useConditionalRender = (
  breakpointData: UseOptimizedBreakpointReturn,
  targetBreakpoint: BreakPointVals | BreakPointVals[],
): boolean => {
  return breakpointData.shouldRenderOnlyFor(targetBreakpoint);
};
